import { FormEvent, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Mail, Key, Eye, EyeOff, Phone } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import { GoogleAuthButton } from "./GoogleAuthButton";
import { useNavigate } from "react-router-dom";
import { logUserLogin } from "@/utils/analyticsUtils";
import { ForgotPasswordDialog } from "./ForgotPasswordDialog";
import { PhoneSignInForm } from "./PhoneSignInForm";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useTranslation } from "react-i18next";
// import { Turnstile } from '@marsidev/react-turnstile'

type SignInFormProps = {
  redirectLoggedInUser: (userId: string) => Promise<void>;
};

export const SignInForm = ({ redirectLoggedInUser }: SignInFormProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(true); // For sign-in, we assume terms were accepted during sign-up
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [authMethod, setAuthMethod] = useState<"email" | "phone">("email");
  // const [captchaToken, setCaptchaToken] = useState();
  // const [turnstileKey, setTurnstileKey] = useState(0);
  const { toast } = useToast();
  const navigate = useNavigate();
  const { t } = useTranslation();
  
  // const devSiteKey  = "1x00000000000000000000AA";
  // const prodSiteKey = "0x4AAAAAABipt3BbaWslaZ-z";

  // let siteKey = "";
  // if (!window.location.hostname.includes("jitume.gebeya.com")) {
  //   siteKey = devSiteKey;
  // } else {
  //   siteKey = prodSiteKey; 
  // }

  // const resetTurnstile = () => {
  //   setCaptchaToken(undefined);
  //   setTurnstileKey(prev => prev + 1);
  // };

  const handleSignIn = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsLoading(true);
    
    const formData = new FormData(e.currentTarget);
    const email = formData.get("email") as string;
    const password = formData.get("password") as string;

    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
        // options: {
        //   captchaToken: captchaToken
        // }
      });

      if (error) {
        toast({
          variant: "destructive",
          title: t('common.error'),
          description: error.message,
        });
        setIsLoading(false);
        // Reset Turnstile on error
        // resetTurnstile();
      } else {
        // Show success toast
        toast({
          title: t('auth.welcomeBackExisting'),
          description: t('auth.signedInSuccess'),
        });
        
        // Log the successful login
        if (data.user) {
          await logUserLogin(data.user.id);
          
          // Use the redirectLoggedInUser function which now uses redirectAfterAuth
          try {
            await redirectLoggedInUser(data.user.id);
          } catch (error) {
            console.error("Error redirecting after login:", error);
            // Fallback navigation if profile check fails
            navigate('/dashboard', { replace: true }); 
          }
        }
        // Reset Turnstile on success
        // resetTurnstile();
      }
    } catch (error) {
      console.error("Sign in error:", error);
      toast({
        variant: "destructive",
        title: t('common.error'),
        description: t('auth.unexpectedError'),
      });
      setIsLoading(false);
      // Reset Turnstile on error
      // resetTurnstile();
    }
  };

  return (
    <div className="space-y-4">
      <GoogleAuthButton 
        isLoading={isLoading} 
        setIsLoading={setIsLoading} 
        termsAccepted={termsAccepted} 
      />

      <div className="relative flex items-center justify-center">
        <Separator className="absolute w-full" />
        <span className="relative bg-white px-2 text-xs text-muted-foreground">{t('auth.or')}</span>
      </div>

      <Tabs defaultValue="email" className="w-full" onValueChange={(value) => setAuthMethod(value as "email" | "phone")}>
        <TabsList className="grid w-full grid-cols-2 mb-4">
          <TabsTrigger value="email" className="flex items-center gap-1">
            <Mail className="h-4 w-4" />
            <span>{t('auth.email')}</span>
          </TabsTrigger>
          <TabsTrigger value="phone" className="flex items-center gap-1">
            <Phone className="h-4 w-4" />
            <span>{t('auth.phone')}</span>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="email" className="space-y-4">
          <form onSubmit={handleSignIn} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="signin-email">{t('auth.email')}</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  id="signin-email"
                  name="email"
                  type="email"
                  placeholder={t('auth.emailPlaceholder')}
                  className="pl-10"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="signin-password">{t('auth.password')}</Label>
              <div className="relative">
                <Key className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  id="signin-password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  className="pl-10 pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-3 text-muted-foreground hover:text-foreground focus:outline-none"
                  aria-label={showPassword ? t('auth.hidePassword') : t('auth.showPassword')}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              <div className="flex justify-end">
                <button 
                  type="button" 
                  onClick={() => setShowForgotPassword(true)}
                  className="text-xs text-gebeya-pink hover:underline focus:outline-none"
                >
                  {t('auth.forgotPassword')}
                </button>
              </div>
            </div>
            {/* <div className="flex justify-center">
              <Turnstile
                key={turnstileKey}
                siteKey={siteKey}
                // onSuccess={(token: any) => setCaptchaToken(token)}
                // onError={() => setCaptchaToken(null)}
              />
            </div> */}
            <Button 
              type="submit" 
              className="w-full bg-gradient-to-r from-gebeya-pink to-gebeya-orange hover:opacity-90" 
              disabled={isLoading}
            >
              {isLoading ? (
                <div className="flex items-center gap-2">
                  <img 
                    src="/lovable-uploads/f8714beb-a432-4da6-acab-3ef6f0a42367.png" 
                    alt="Loading" 
                    className="h-4 w-4 gebeya-spinner" 
                  />
                  <span>{t('auth.signingIn')}</span>
                </div>
              ) : t('auth.signInWithEmail')}
            </Button>
          </form>
        </TabsContent>

        <TabsContent value="phone">
          <PhoneSignInForm redirectLoggedInUser={redirectLoggedInUser} />
        </TabsContent>
      </Tabs>

      <ForgotPasswordDialog 
        isOpen={showForgotPassword} 
        onClose={() => setShowForgotPassword(false)} 
      />
    </div>
  );
};
